import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Bug, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/admin-shell";
import { supabase } from "@/integrations/supabase/client";
import { azError } from "@/lib/az-errors";

export const Route = createFileRoute("/_authenticated/admin/xetalar")({
  component: Page,
});

type ErrRow = {
  id: string;
  message: string;
  stack: string | null;
  path: string | null;
  user_agent: string | null;
  resolved: boolean;
  created_at: string;
};

function Page() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-errors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_errors")
        .select("id, message, stack, path, user_agent, resolved, created_at")
        .order("created_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      return (data ?? []) as ErrRow[];
    },
  });

  const resolve = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("client_errors").update({ resolved: true }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Xəta həll edilmiş kimi işarələndi");
      void qc.invalidateQueries({ queryKey: ["admin-errors"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("client_errors").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Silindi");
      void qc.invalidateQueries({ queryKey: ["admin-errors"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  const rows = data ?? [];
  const open = rows.filter((r) => !r.resolved);
  const last24 = rows.filter((r) => Date.now() - new Date(r.created_at).getTime() < 864e5);

  return (
    <div>
      <AdminPageHeader
        title="Xəta monitorinqi"
        description="İstifadəçilərin brauzerində baş verən xətalar"
      />

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Ümumi", value: rows.length, icon: Bug },
          { label: "Həll olunmayan", value: open.length, icon: AlertTriangle },
          { label: "Son 24 saat", value: last24.length, icon: AlertTriangle },
        ].map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card p-4 shadow-card">
            <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
              <c.icon className="size-4" />
              {c.label}
            </div>
            <p className="mt-2 text-2xl font-extrabold">{isLoading ? "…" : c.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-2">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Yüklənir…</p>
        ) : rows.length === 0 ? (
          <p className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
            Hələ xəta qeyd olunmayıb. 🎉
          </p>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              className={`rounded-2xl border p-3 shadow-card ${
                r.resolved ? "border-border bg-muted/40" : "border-destructive/40 bg-card"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold">{r.message}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {r.path ?? "—"} ·{" "}
                    {new Date(r.created_at).toLocaleString("az-AZ", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  {!r.resolved && (
                    <button
                      type="button"
                      aria-label="Həll olundu"
                      onClick={() => resolve.mutate(r.id)}
                      className="grid size-9 place-items-center rounded-xl border border-border hover:bg-accent"
                    >
                      <Check className="size-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label="Sil"
                    onClick={() => remove.mutate(r.id)}
                    className="grid size-9 place-items-center rounded-xl border border-border text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </div>
              {r.stack && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs font-semibold text-muted-foreground">
                    Texniki detal
                  </summary>
                  <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded-xl bg-muted p-2 text-[10px]">
                    {r.stack}
                  </pre>
                </details>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
